import React, { useEffect, useMemo, useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, Link2, X } from 'lucide-react';
import { listCharts } from '../api';
import { listEntries } from '../entriesApi';

/**
 * Slice B.2 — Linked Entry renderer.
 * Emits the B.1 backend contract: { template_code, entry_id }.
 *
 * Candidates are loaded ONLY for the current participant: we list the
 * participant's charts of `fd.linked_template` and aggregate their recent
 * entries. No cross-participant entries are ever fetched or exposed.
 */

const MAX_CANDIDATES = 25;

function entryLabel(e) {
  const when = e.entry_date ? new Date(e.entry_date).toLocaleString() : '';
  return e.summary ? `${e.summary}${when ? ` · ${when}` : ''}` : (when || e.id);
}

export function LinkedEntryField({ fd, value, onChange, participantId }) {
  const linkedTemplate = fd?.linked_template;
  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [query, setQuery] = useState('');
  const v = value && typeof value === 'object' ? value : {};

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!participantId || !linkedTemplate) { setCandidates([]); return; }
      setLoading(true);
      try {
        const charts = await listCharts(participantId);
        const matching = (charts || []).filter(c => c.template_code === linkedTemplate);
        const all = [];
        for (const c of matching) {
          const entries = await listEntries(participantId, c.id, { limit: MAX_CANDIDATES });
          for (const e of entries) all.push(e);
        }
        all.sort((a, b) => new Date(b.entry_date || 0) - new Date(a.entry_date || 0));
        if (!cancelled) setCandidates(all.slice(0, MAX_CANDIDATES));
      } catch {
        if (!cancelled) setCandidates([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [participantId, linkedTemplate]);

  const selected = useMemo(
    () => candidates.find(c => c.id === v.entry_id) || null,
    [candidates, v.entry_id],
  );

  const filtered = useMemo(() => {
    if (!query.trim()) return candidates;
    const q = query.toLowerCase();
    return candidates.filter(c => entryLabel(c).toLowerCase().includes(q));
  }, [candidates, query]);

  const pick = (e) => onChange({ template_code: linkedTemplate, entry_id: e.id });
  const clear = () => onChange(null);

  return (
    <div className="rounded-md border p-3 space-y-2" data-testid="chart-field-linked-entry">
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <Link2 className="w-3.5 h-3.5" /> Linked {linkedTemplate?.replace(/_/g, ' ') || 'entry'} (same participant)
      </div>

      {v.entry_id ? (
        <div className="flex items-center justify-between rounded border bg-indigo-50/50 px-2 py-1.5">
          <span className="text-xs text-slate-700 truncate" data-testid="chart-field-linked-entry-selected">
            {selected ? entryLabel(selected) : `Entry ${v.entry_id}`}
          </span>
          <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-slate-500" onClick={clear} data-testid="chart-field-linked-entry-clear">
            <X className="w-3.5 h-3.5" />
          </Button>
        </div>
      ) : (
        <>
          <Input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search recent entries…"
            className="h-9 text-xs"
            data-testid="chart-field-linked-entry-search"
          />
          <div className="max-h-40 overflow-y-auto rounded border divide-y">
            {loading ? (
              <div className="p-3 text-center text-xs text-muted-foreground">
                <Loader2 className="w-3.5 h-3.5 inline animate-spin mr-1" /> Loading…
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-3 text-center text-xs text-muted-foreground italic" data-testid="chart-field-linked-entry-empty">
                No linked entries available for this participant.
              </div>
            ) : (
              filtered.map(e => (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => pick(e)}
                  className="w-full text-left px-2 py-1.5 text-xs hover:bg-slate-50"
                  data-testid={`chart-field-linked-entry-option-${e.id}`}
                >
                  {entryLabel(e)}
                </button>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default LinkedEntryField;
